import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { Button, Divider, FieldLabel, Logo, RawLink, spacing, Stack, Text } from '@cash-design-system/react'
import { SmsDeliveryMechanism } from '@cash-web/protos/squareup/franklin/common/sms_delivery_mechanism.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useAuthenticatePasskey } from '@cash-web/shared-auth-feature-main'
import {
  hasCommerceIntent,
  hasTaxIntent,
  optionalScopeHeaders,
  trySetOnboardingContext,
} from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterEmail } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterEmail'
import { useMutationRegisterSms } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterSms'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useArcadeMigrationFeatureFlag, useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { regionConfig, useCountryFromRegionProvider } from '@cash-web/shared-data-access-region-provider'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { isSupportedRegion, PhoneInput, PhoneValue, SegmentedControl } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { formatPhoneNumberE164, isUndefinedOrEmpty } from '@cash-web/shared-util-formatting'
import { getQueryParam, getSupportedInternalRedirect } from '@cash-web/shared-util-url'
import { isValidEmail } from '@cash-web/shared-util-validation'
import { WebInteractClick, WebViewViewComponent } from '@squareup/customer-data-events-web'

import { getRegisterEmailErrors, getRegisterSMSErrors } from '../../util/statusErrors'
import { BaseBlocker, EmailForm, SubmitButton } from '../'
import { Alias, clearAlias, getAlias, getLastLoginType, setLastLoginType } from './aliasUtils'
import { EmailInput } from './EmailInput'
import { LegalText } from './LegalText'

const AliasLink = ({
  trackingEvent,
  children,
  onClick,
}: React.PropsWithChildren<{ trackingEvent: string; onClick: () => void }>) => {
  const logCdfEvent = useLogCashWebCdfEvent()

  function handleClick() {
    onClick()
    logCdfEvent(WebInteractClick({ pageName: 'alias_blocker', componentName: trackingEvent }))
  }

  return <RawLink onClick={handleClick}>{children}</RawLink>
}

export function BlockerAlias(): React.ReactElement {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { search } = useLocation()
  const { error } = useContext(ErrorContext)
  const logCdfEvent = useLogCashWebCdfEvent()
  const region = useCountryFromRegionProvider()
  const customerRegionCode = useCountryFromRegionProvider()

  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const convergenceSkipLoginEnabled = useFeatureFlag('cash-web-login-convergence-skip-login')
  const tryLogInWithPasskey = useFeatureFlag('cash-web-passkeys-try-login-with-passkeys') === 'ENABLED'

  const phoneCountryCode = customerRegionCode && isSupportedRegion(customerRegionCode) ? customerRegionCode : 'US'
  const regionConfigEntry = customerRegionCode ? regionConfig[customerRegionCode] : undefined
  const alias = getAlias()
  const lastLoginType = getLastLoginType()
  const initialAliasType = alias?.type || lastLoginType || 'phone'
  const [aliasStateType, setAliasStateType] = useState<Alias['type']>(initialAliasType)

  const auth = useAuth()
  const started = useRef(false)
  const defaultValues = useMemo(
    () => ({
      regionCode: phoneCountryCode,
      phone: convergenceSkipLoginEnabled && alias?.type === 'phone' ? alias.value : '',
      email: convergenceSkipLoginEnabled && alias?.type === 'email' ? alias.value : '',
      aliasInputType: initialAliasType as Alias['type'],
    }),
    [alias?.type, alias?.value, initialAliasType, convergenceSkipLoginEnabled, phoneCountryCode]
  )
  const form = useForm<PhoneValue & { aliasInputType: Alias['type'] } & { email: string }>({
    mode: 'onTouched',
    defaultValues,
  })
  const { watch, reset, getValues, handleSubmit } = form
  const phoneValue = watch('phone')
  const emailValue = watch('email')
  const aliasInputType = watch('aliasInputType')
  const aliasType = isArcadeMigrationEnabled ? aliasInputType : aliasStateType

  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const flow = useFlowContext()
  const { flowToken, clientScenario } = flow.active ?? {}

  const newCustomerRegistration = getQueryParam('su') !== null
  const regionCode = watch('regionCode')
  const [isSkippingPasskeyAndFallingBackToConfirmationCode, setIsSkippingPasskeyAndFallingBackToConfirmationCode] =
    useState(false)

  const { mutateAsync: registerEmail } = useMutationRegisterEmail({
    options: { clientScenario, flowToken, optionalRequestHeaders: optionalScopeHeaders() },
  })
  const { mutateAsync: registerSms } = useMutationRegisterSms({
    options: { clientScenario, flowToken, optionalRequestHeaders: optionalScopeHeaders() },
  })

  useEffect(() => {
    if (!convergenceSkipLoginEnabled) {
      return
    }

    // submit form while fetching will likely trigger a 429 error
    if (alias === null || started.current || auth.isFetching) {
      return
    }
    started.current = true
    const resetValues =
      alias.type === 'phone' ? { ...defaultValues, phone: alias.value } : { ...defaultValues, email: alias.value }

    const submitHandler = alias.type === 'phone' ? handlePhoneSubmit : handleEmailSubmit

    reset(resetValues)
    handleSubmit(submitHandler)().finally(() => {
      clearAlias()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isFetching, convergenceSkipLoginEnabled])

  async function handleRegisterEmail(email: string, handleFailure?: () => void) {
    await trySetOnboardingContext({ flowToken })
    return await handleBlockerResponse({
      promisedBlockerUpdate: registerEmail(
        {
          email_address: email,
          payment_tokens: flow?.active?.paymentTokens,
        },
        {}
      ),
      errors: getRegisterEmailErrors(genericError, t),
      onFailure: handleFailure,
    })
  }

  async function handleRegisterSms(formattedNumber: string, handleFailure?: () => void) {
    await trySetOnboardingContext({ flowToken })
    return await handleBlockerResponse({
      promisedBlockerUpdate: registerSms({
        sms_number: formattedNumber,
        verification_mechanism: SmsDeliveryMechanism.SMS_TEXT,
        payment_tokens: flow?.active?.paymentTokens,
      }),
      errors: getRegisterSMSErrors(genericError, t),
      onFailure: handleFailure,
    })
  }

  const { authenticatePasskey, isLoading: isPasskeyLoading } = useAuthenticatePasskey({
    onSuccess: (data, flowToken) => {
      flow.updateWithResponse({ response: Promise.resolve(data), flowToken, queriesToInvalidate: [] })
    },
    // On error, skip trying to log in with passkeys and go straight to
    // verification code step.
    onError: () => {
      setIsSkippingPasskeyAndFallingBackToConfirmationCode(true)
      if (aliasType === 'email') {
        handleSubmit(() =>
          handleRegisterEmail(getValues().email, () => {
            setIsSkippingPasskeyAndFallingBackToConfirmationCode(false)
          })
        )()
      }
      if (aliasType === 'phone') {
        handleSubmit(() => {
          const { phone, regionCode } = getValues()
          const formattedNumber = formatPhoneNumberE164(phone, regionCode)
          return handleRegisterSms(formattedNumber, () => setIsSkippingPasskeyAndFallingBackToConfirmationCode(false))
        })()
      }
    },
    onShowPasskeyDialog: () => {
      logCdfEvent(
        WebViewViewComponent({ pageName: 'alias_blocker', componentName: 'Passkey Authenticate System Dialog' })
      )
    },
  })

  const handleSetAliasType = useCallback(
    (type: Alias['type'], prefilledEntry = '') => {
      const resetValues = { ...defaultValues, regionCode, aliasInputType: type }
      if (prefilledEntry) {
        resetValues[type] = prefilledEntry
      }
      reset(resetValues)

      if (!isArcadeMigrationEnabled) {
        setAliasStateType(type)
      }
    },
    [defaultValues, isArcadeMigrationEnabled, regionCode, reset]
  )

  const handlePhoneSubmit = async ({ phone, regionCode }: PhoneValue) => {
    setLastLoginType('phone')
    let formattedNumber = formatPhoneNumberE164(phone, regionCode)
    if (tryLogInWithPasskey) {
      // Remove the `+1` prefix from US phone numbers
      if (regionCode === 'US' && formattedNumber.length === 12) {
        formattedNumber = formattedNumber.slice(2)
      }
      return authenticatePasskey({ phone: formattedNumber })
    } else {
      return await handleRegisterSms(formattedNumber)
    }
  }

  const handleEmailSubmit = async (data: EmailForm) => {
    setLastLoginType('email')
    const { email } = data
    if (tryLogInWithPasskey) {
      return authenticatePasskey({ email })
    } else {
      return await handleRegisterEmail(email)
    }
  }

  const isTaxLogin = hasTaxIntent()
  const getTitle = () => {
    if (newCustomerRegistration) {
      return t('Create an account', {
        context: 'Title text.',
      })
    }

    if (isTaxLogin) {
      return aliasType === 'phone'
        ? t('Enter the phone number you use for Cash App', { context: 'Title text.' })
        : // email
          t('Enter the email you use for Cash App', { context: 'Title text.' })
    }

    const internalRedirectParam = getSupportedInternalRedirect()

    switch (internalRedirectParam) {
      case 'support':
        return t('Log in to contact Cash App support', { context: 'Title text.' })
      case '/account/tax-information':
        return getQueryParam('use_business_account')
          ? t('To view your tax info, log in using your Cash for Business account.', { context: 'Title text.' })
          : t('Log in to enter your Tax information', { context: 'Title text.' })
      case '/account/individual-tax-information':
        return t('Log in to enter your Tax information', { context: 'Title text.' })
    }

    if (tryLogInWithPasskey) {
      return t('Log in', { context: 'Title text.' })
    }

    return aliasType === 'phone'
      ? t('Log in using your phone', { context: 'Title text.' })
      : t('Log in using your email', { context: 'Title text.' })
  }

  const getSubtitle = () => {
    if (newCustomerRegistration) {
      return t('To create an account, enter your phone number or email address.', {
        context: 'Login flow first step. Subtitle text explaining the next step in process.',
      })
    }
    if (tryLogInWithPasskey) {
      return t('Enter your phone number or email.', {
        context: 'Login flow first step. Subtitle text explaining the next step in process.',
      })
    }
    return t('To keep your account secure, we’ll send you a code to log in.', {
      context: 'Login flow first step. Subtitle text explaining the next step in process.',
    })
  }

  const createAccountLink =
    !hasCommerceIntent() && regionConfigEntry?.showCreateAccountLink ? (
      <>
        {' '}
        <Trans
          i18nKey={'New to Cash App? <rawlink>Create account</rawlink>'}
          context="Part of the subtitle text. Prompt for customers without an account, and a Link to Create Account flow."
          components={{
            rawlink: (
              <AliasLink
                key="create account link"
                trackingEvent="Create Account"
                onClick={() => {
                  const searchParams = new URLSearchParams(search)
                  const queryParams = searchParams.size > 0 ? `?su&${searchParams.toString()}` : '?su'
                  navigate(`${window.location.pathname}${queryParams}`, {
                    state: { createAccount: true },
                    replace: true,
                  })
                }}
              />
            ),
          }}
        />
      </>
    ) : null

  const loginLink = (
    <>
      {' '}
      <Trans
        i18nKey={'Already have an account? <rawlink>Log in</rawlink>'}
        context="Part of the subtitle text. Prompt for customers without an account, and a Link to Login flow."
        components={{
          rawlink: (
            <AliasLink
              key="login"
              trackingEvent="Login"
              onClick={() => {
                const searchParams = new URLSearchParams(search)
                searchParams.delete('su')
                navigate(window.location.pathname + '?' + searchParams.toString(), {
                  replace: true,
                })
              }}
            />
          ),
        }}
      />
    </>
  )

  const loginRedirect = newCustomerRegistration ? loginLink : createAccountLink
  const subtitle = (
    <div>
      {getSubtitle()}
      {loginRedirect}
    </div>
  )

  const isPasskeyAuthenticating =
    tryLogInWithPasskey && (isPasskeyLoading || isSkippingPasskeyAndFallingBackToConfirmationCode)
  const isAutoSubmittingAlias = convergenceSkipLoginEnabled && alias !== null && !error
  const loadingOverride = isPasskeyAuthenticating || isAutoSubmittingAlias

  if (isArcadeMigrationEnabled) {
    return (
      <>
        <Logo
          colorVariant={theme.darkMode ? 'white' : 'black'}
          variant="default"
          altText="Cash App"
          height={40}
          width={40}
        />
        <BaseBlocker
          mainText={getTitle()}
          onSubmit={aliasType === 'phone' ? handlePhoneSubmit : handleEmailSubmit}
          form={form}
          buttons={[
            <SubmitButton
              key={`submit-${aliasType}-btn`}
              isLoading={loadingOverride}
              disabled={isUndefinedOrEmpty(aliasType === 'phone' ? phoneValue : emailValue)}
            />,
          ]}
        >
          <Stack space={spacing.defaultContent}>
            <SegmentedControl
              key={`alias-type-${aliasType}`}
              defaultValue={initialAliasType}
              name="aliasInputType"
              options={[
                { label: 'Phone number', value: 'phone' },
                { label: 'Email', value: 'email' },
              ]}
              onChange={() => handleSetAliasType(aliasType === 'phone' ? 'email' : 'phone')}
              rounded
            />
            {aliasType === 'phone' ? (
              <PhoneInput
                isLabelHidden
                autoFocus
                disableRegionCode
                onPaste={e => {
                  const clipboardText = e.clipboardData.getData('text').trim()
                  if (isValidEmail(clipboardText)) {
                    e.preventDefault()
                    handleSetAliasType('email', clipboardText)
                  }
                }}
                placeholder={t('Enter phone number', { context: 'Phone number input placeholder' })}
                readOnly={loadingOverride}
              />
            ) : (
              <EmailInput handleSetAliasType={handleSetAliasType} loadingOverride={loadingOverride} />
            )}
          </Stack>
        </BaseBlocker>
        <Stack space={spacing.xl}>
          <LegalText region={region || 'US'} />
          <Divider />
          <Text typography="bodyDefault" color="textStandard">
            {loginRedirect}
          </Text>
        </Stack>
      </>
    )
  }

  return aliasType === 'phone' ? (
    <BaseBlocker
      mainText={getTitle()}
      secondaryText={subtitle}
      onSubmit={handlePhoneSubmit}
      form={form}
      buttons={[
        <Button
          key="switch-email-btn"
          variant="secondary"
          onClick={() => handleSetAliasType('email')}
          disabled={loadingOverride}
        >
          {t('Use email', { context: 'Button label.' })}
        </Button>,
        <SubmitButton key="submit-phone-btn" isLoading={loadingOverride} />,
      ]}
    >
      <PhoneInput
        autoFocus
        onPaste={e => {
          const clipboardText = e.clipboardData.getData('text').trim()
          if (isValidEmail(clipboardText)) {
            e.preventDefault()
            handleSetAliasType('email', clipboardText)
          }
        }}
        readOnly={loadingOverride}
      />
    </BaseBlocker>
  ) : (
    <BaseBlocker
      mainText={getTitle()}
      secondaryText={subtitle}
      onSubmit={handleEmailSubmit}
      form={form}
      buttons={[
        <Button
          key="switch-phone-btn"
          variant="secondary"
          onClick={() => handleSetAliasType('phone')}
          disabled={loadingOverride}
        >
          {t('Use phone', { context: 'Button label.' })}
        </Button>,
        <SubmitButton key="submit-email-btn" isLoading={loadingOverride} />,
      ]}
    >
      <FieldLabel label={t('Email', { context: 'Input Label.' })}>
        <EmailInput handleSetAliasType={handleSetAliasType} loadingOverride={loadingOverride} />
      </FieldLabel>
    </BaseBlocker>
  )
}
